import { Monitor, Moon, PaintBucket, Palette, Sun } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ThemeMode, ThemePresetId } from '@/shared/theme/theme-config'
import { CustomThemeSheet } from '@/features/theme/ui/custom-theme-sheet'
import { useSaveWorkspaceBranding } from '@/features/theme/model/use-save-workspace-branding'
import { useTheme } from '@/shared/theme/use-theme'
import { useCurrentUserProfile } from '@/entities/user/model/use-current-user-profile'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'

const modeOptions: Array<{
  value: ThemeMode
  labelKey: string
  icon: typeof Sun
}> = [
  { value: 'light', labelKey: 'light', icon: Sun },
  { value: 'dark', labelKey: 'dark', icon: Moon },
  { value: 'system', labelKey: 'system', icon: Monitor },
]

const presetOptions: Array<{
  value: ThemePresetId
  labelKey: string
}> = [
  { value: 'current', labelKey: 'monochrome' },
  { value: 'default', labelKey: 'default' },
  { value: 'workspace', labelKey: 'workspace' },
]

function ThemeDropdownContent({
  mode,
  preset,
  workspaceName,
  onOpenBranding,
  onModeChange,
  onPresetChange,
  t,
}: {
  mode: ThemeMode
  preset: ThemePresetId
  workspaceName: string
  onOpenBranding: () => void
  onModeChange: (value: ThemeMode) => void
  onPresetChange: (value: ThemePresetId) => void
  t: (key: string) => string
}) {
  return (
    <DropdownMenuContent align="end" className="w-60">
      <DropdownMenuLabel>{t('appearance')}</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={mode}
        onValueChange={(value) => onModeChange(value as ThemeMode)}
      >
        {modeOptions.map((option) => {
          const Icon = option.icon

          return (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              <Icon className="size-4" />
              {t(option.labelKey)}
            </DropdownMenuRadioItem>
          )
        })}
      </DropdownMenuRadioGroup>

      <DropdownMenuSeparator />

      <DropdownMenuLabel>{t('theme')}</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={preset}
        onValueChange={(value) => onPresetChange(value as ThemePresetId)}
      >
        {presetOptions.map((option) => (
          <DropdownMenuRadioItem key={option.value} value={option.value}>
            {option.value === 'workspace' ? workspaceName : t(option.labelKey)}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>

      <DropdownMenuSeparator />

      <DropdownMenuItem
        onSelect={(event) => {
          event.preventDefault()
          onOpenBranding()
        }}
      >
        <PaintBucket className="size-4" />
        {t('workspaceBranding')}
      </DropdownMenuItem>
    </DropdownMenuContent>
  )
}

export function ThemeToggle() {
  const { t } = useTranslation('theme')
  const {
    editableTokens,
    effectiveMode,
    mode,
    preset,
    resetCustomTokens,
    setMode,
    setPreset,
  } = useTheme()
  const { currentUser } = useCurrentUserProfile()
  const [isCustomSheetOpen, setIsCustomSheetOpen] = useState(false)

  const workspaceId = currentUser?.workspaceId
  const workspaceName = currentUser?.workspace?.name ?? t('workspace')
  const {
    save: handleSave,
    reset: handleReset,
    isSaving,
  } = useSaveWorkspaceBranding({ resetCustomTokens, workspaceId })

  const canConfigure =
    currentUser?.can('configure', 'workspaceSettings') ?? false

  const EffectiveModeIcon = effectiveMode === 'dark' ? Moon : Sun

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('theme:themeSettingsAria')}
          >
            <div className="relative">
              <Palette className="size-4" />
              <EffectiveModeIcon className="absolute -bottom-1 -right-1 size-3 rounded-full bg-background p-0.5 text-muted-foreground" />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <ThemeDropdownContent
          mode={mode}
          preset={preset}
          workspaceName={workspaceName}
          onOpenBranding={() => setIsCustomSheetOpen(true)}
          onModeChange={setMode}
          onPresetChange={setPreset}
          t={t}
        />
      </DropdownMenu>

      <CustomThemeSheet
        key={isCustomSheetOpen ? 'custom-open' : 'custom-closed'}
        open={isCustomSheetOpen}
        onOpenChange={setIsCustomSheetOpen}
        editableTokens={editableTokens}
        onSave={handleSave}
        onReset={handleReset}
        onSetCustomPreset={() => setPreset('workspace')}
        canConfigure={canConfigure}
        isSaving={isSaving}
      />
    </>
  )
}
